import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { debounceTime, distinctUntilChanged, switchMap } from "rxjs/operators";
import { Subject, EMPTY } from "rxjs";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { SaleOrderLineAddressDto } from "../../../../core/models/orders/saleOrderLineAddressDto";
import { GetAllVendorsDto } from "../../../../core/models/vendor/getAllVendors";
import { SamplepoListingService } from "../../../../core/services/samplepo-listing.service";
import { TableModule } from "primeng/table";
import { FocusOnShowDirective } from "../../../../core/directives/auto-focus.directive";
import { CommonModule } from "@angular/common";
import { ModalStackerComponent } from "../../../../shared/components/modal-stacker/modal-stacker.component";
import { Popover, PopoverModule } from "primeng/popover";
import { SelectModule } from "primeng/select";
import { Address } from "../../../../core/models/shared/address";
import { InputTextModule } from "primeng/inputtext";
import { DropdownModule } from "primeng/dropdown";
import { VendorSummaryComponent } from "../../../vendors/components/vendor-summary/vendor-summary.component";

@UntilDestroy()
@Component({
  selector: "jni-order-line-vendor",
  templateUrl: "./order-line-vendor.component.html",
  styleUrls: ["./order-line-vendor.component.scss"],
  imports: [
    ReactiveFormsModule,
    TableModule,
    FocusOnShowDirective,
    CommonModule,
    SelectModule,
    ModalStackerComponent,
    PopoverModule,
    InputTextModule,
    DropdownModule,
    VendorSummaryComponent,
  ],
})
export class OrderLineVendorComponent implements OnInit {
  showSmallLoader = false;
  vendorList: GetAllVendorsDto[] = [];
  @Input() isFromCdi = false;
  @Input() vendorAccountNumber = "";
  @Input() poAssignment = "New";
  @Input() formErrors!: Record<string, string>;
  @Input() saleOrderLine: SaleOrderLineAddressDto[] = [];
  @Input() createdDate = "";
  @Input() createdBy = "";
  @Input() modifiedBy = "";
  @Input() modifiedDate = "";
  @Input() isEdit = false;
  @Input() orderType = "";
  @Input() addNewLineCase = false;
  @Input() collapseStatus = false;

  vendorDisplayModal = false;

  poAssigment: { id: number; name: string; status: boolean }[] = [
    {
      id: 0,
      name: "Default",
      status: false,
    },
    {
      id: 1,
      name: "New",
      status: false,
    },
    {
      id: 2,
      name: "Existing",
      status: false,
    },
  ];
  public searchVendor$ = new Subject<string>();
  @Input() parent!: FormGroup;
  @Output() vendorAccountEvent = new EventEmitter<KeyboardEvent>();
  @Output() vendorObjectEvent = new EventEmitter<GetAllVendorsDto>();
  @Output() validationErrorEvent = new EventEmitter();
  @Output() poAssignmentEvent = new EventEmitter();
  @Output() currencyFromVendorEvent = new EventEmitter<string>();
  @Output() isVendorSelectedEvent = new EventEmitter<boolean>();

  constructor(private _samplepoListingService: SamplepoListingService) {}

  ngOnInit(): void {
    this.searchVendorName();
  }

  searchVendorName() {
    this.searchVendor$
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap((searchitem: string) => {
          this.showSmallLoader = true;
          const accountNumberOrName = searchitem;
          this.vendorList = [];
          if (accountNumberOrName != "" && accountNumberOrName != undefined) {
            return this._samplepoListingService.getVendorByNameAndAccountNumberFilter(
              accountNumberOrName
            );
          }
          return EMPTY;
        })
      )
      .subscribe({
        next: res => {
          if (res.data != null) {
            this.vendorList = res.data as unknown as GetAllVendorsDto[];
          }
          this.showSmallLoader = false;
        },
        error: () => {
          this.showSmallLoader = false;
        },
      });
  }

  onVendorSearchOpenToggle(mydrop: Popover, event: KeyboardEvent) {
    const target = event.target as HTMLInputElement;
    if (target!.value === "") {
      this.isVendorSelectedEvent.emit(false);
      this.parent.patchValue({
        vendor: {
          duplicateVendorDirectDelivery: "",
          vendorDirectDelivery: "",
          vendorPoAssignment: "",
          vendorName: "",
          vendorAddress: "",
          vendorPhone: "",
          vendorEmail: "",
          vendorFax: "",
        },
      });
    }
    mydrop.show(event);
    this.vendorAccountEvent.emit(event);
  }

  redirectToVendorSummary() {
    // this._router.navigate([
    //   `vendors/vendor-summary/${this.vendorAccountNumber}`
    // ]);
    const vendorAccount = this.parent.get([
      "vendor",
      "vendorDirectDelivery",
    ])?.value;
    if (vendorAccount) {
      this.vendorDisplayModal = true;
    }
  }

  closeModal() {
    this.vendorDisplayModal = false;
  }

  poAssignmentStatus() {
    this.poAssigment[2].status = true;
  }

  onVendorRowSelection(
    vendorSelectedRow: GetAllVendorsDto,
    vendorToggle: Popover
  ) {
    this.showSmallLoader = true;
    this.clearVendorForm();
    const partyNumber = vendorSelectedRow.partyNumber;
    const accountNumber = vendorSelectedRow.vendorAccountNumber ?? "";
    this.patchPoAssignment(accountNumber ?? "");
    this.vendorObjectEvent.emit(vendorSelectedRow);
    this.currencyFromVendorEvent.emit(vendorSelectedRow.currency ?? "");
    this._samplepoListingService
      .getVendorAddressByPartyNumber(partyNumber, accountNumber)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.showSmallLoader = false;

          if (res.data) {
            const primaryAddress = res.data.filter(
              (x: Address) =>
                x.isPrimaryD365?.toLowerCase() === "yes" ||
                x.isPrimaryD365?.toLowerCase() === "1"
            );
            if (primaryAddress.length > 0) {
              if (primaryAddress[0].addressD365) {
                this.patchValuesInVendorFormAddressField(
                  primaryAddress[0].addressD365
                );
              }
            }
          }
        },
        error: () => {
          this.showSmallLoader = false;
        },
      });
    this.patchValuesInVendorForm(vendorSelectedRow);
    this.patchAccountNumber(accountNumber);
    vendorToggle.hide();
    this.isVendorSelectedEvent.emit(true);
  }

  patchPoAssignment(vendorNumber: string) {
    let lineStatus = false;
    this.saleOrderLine.forEach(x => {
      if (x.salesOrderLine?.vendorDirectDeliveryD365 === vendorNumber) {
        lineStatus = true;
      }
    });

    if (this.isFromCdi && lineStatus) {
      this.poAssignment = "Existing";
    } else {
      if (lineStatus) {
        this.poAssignment = "Default";
      } else this.poAssignment = "New";
    }
  }

  clearVendorForm() {
    this.parent.patchValue({
      header: {
        vendorName: "",
        vendorPhone: "",
        vendorEmail: "",
        vendorAddress: "",
        vendorFax: "",
      },
    });
  }

  patchValuesInVendorFormAddressField(address: string) {
    this.parent.patchValue({
      vendor: {
        vendorAddress: address,
      },
    });
  }

  patchValuesInVendorForm(vendorSelectedRow: GetAllVendorsDto) {
    this.parent.patchValue({
      vendor: {
        vendorName: vendorSelectedRow.vendorName,
        vendorPhone: vendorSelectedRow.phoneNumber,
        vendorEmail: vendorSelectedRow.emailAddress,
        vendorFax: vendorSelectedRow.fax,
      },
    });
  }

  patchAccountNumber(acountNumber: string) {
    this.vendorAccountNumber = acountNumber;
    this.parent.patchValue({
      vendor: {
        vendorDirectDelivery: acountNumber,
        duplicateVendorDirectDelivery: acountNumber,
      },
    });
  }

  logValidationErrors() {
    this.validationErrorEvent.emit();
  }
}
